import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Ban, Eye, Loader2, Play, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { ADMIN_CUSTOMERS_KEY, adminCustomersApi } from '@/lib/admin-customers-api';
import { getErrorMessage } from '@/lib/errors';
import { impersonationApi } from '@/lib/impersonation-api';
import { useAuthStore } from '@/stores/auth-store';
import { catalogApi } from '@/lib/catalog-api';
import type { BillingInterval, Customer, CustomerStatus, Plan } from '@/types/api';

const tone: Record<CustomerStatus, 'success' | 'warning' | 'danger' | 'muted'> = {
  active: 'success',
  suspended: 'danger',
  archived: 'muted',
};

const STATUS_FILTERS: { value: CustomerStatus | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'archived', label: 'Archived' },
];

const INTERVAL_FILTERS: { value: BillingInterval | ''; label: string }[] = [
  { value: '', label: 'Any term' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'lifetime', label: 'Lifetime' },
];

/**
 * Customer administration.
 *
 * The plan and term shown against each customer come from the API already resolved — this page
 * never turns a plan id into a name, nor decides whether a subscription is still live. Both are
 * Billing's facts, and a second opinion here is how an admin list ends up disagreeing with the
 * billing screen.
 *
 * There is no delete. Archiving retains everything, because a customer's invoices are financial
 * records and their subscriptions and servers are the history behind them.
 */
export function AdminCustomersPage() {
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<CustomerStatus | ''>('');
  const [interval, setInterval] = useState<BillingInterval | ''>('');
  const [planId, setPlanId] = useState('');
  const [search, setSearch] = useState('');
  const [reason, setReason] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null);

  const customers = useQuery({
    queryKey: [...ADMIN_CUSTOMERS_KEY, status, interval, planId, search],
    queryFn: () =>
      adminCustomersApi.list({
        status: status || undefined,
        interval: interval || undefined,
        planId: planId || undefined,
        search: search || undefined,
      }),
  });

  const products = useQuery({ queryKey: ['catalog-products'], queryFn: () => catalogApi.listProducts() });
  const plans: Plan[] = (products.data ?? []).flatMap((p) => p.plans);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ADMIN_CUSTOMERS_KEY });
    // Suspending a customer changes what their subscription screen means, and archiving is
    // visible there too.
    void queryClient.invalidateQueries({ queryKey: ['admin-subscriptions'] });
  };

  const act = useMutation({
    mutationFn: (fn: () => Promise<Customer>) => fn(),
    onSuccess: invalidate,
  });

  const navigate = useNavigate();

  const impersonate = useMutation({
    mutationFn: async ({ customerId, why }: { customerId: string; why: string }) => {
      const grant = await impersonationApi.start(customerId, why);
      const { accessToken, refreshToken, user } = useAuthStore.getState();

      useAuthStore.getState().beginImpersonation(
        {
          sessionId: grant.impersonation_session_id,
          subjectEmail: grant.subject_email,
          actorEmail: grant.actor_email,
          expiresAt: Date.now() + grant.expires_in * 1000,
          restore: { accessToken, refreshToken, user },
        },
        grant.access_token,
      );

      return grant;
    },
    onSuccess: () => {
      // Nothing cached was fetched as this customer, and the admin's own data must not be
      // shown under their name.
      queryClient.clear();
      // Straight to Servers: "why can't they see their server" is what this is for.
      navigate('/servers');
    },
  });

  const create = useMutation({
    mutationFn: () =>
      adminCustomersApi.create({
        name: name.trim(),
        email: email.trim(),
        password,
        ...(company.trim() === '' ? {} : { company: company.trim() }),
      }),
    onSuccess: () => {
      // Shown once, because this is the only moment the platform knows the password — it is
      // stored as a hash and can never be displayed again. An administrator who navigates away
      // without copying it has to set a new one.
      setCreated({ email: email.trim(), password });
      setName('');
      setEmail('');
      setPassword('');
      setCompany('');
      invalidate();
    },
  });

  const busy = act.isPending || create.isPending || impersonate.isPending;
  const error = act.error ?? create.error ?? impersonate.error;
  const reasonGiven = reason.trim() !== '';
  const canCreate =
    name.trim() !== '' && email.trim() !== '' && password.length >= 12 && !busy;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>
        <p className="text-sm text-muted-foreground">
          Every account on the platform, what they are paying for, and when they signed up.
        </p>
      </div>

      {error && <Alert variant="danger">{getErrorMessage(error)}</Alert>}

      {created && (
        <Alert variant="success">
          <p className="font-medium">Customer created. Give them these details now.</p>
          <p className="mt-1 font-mono text-xs">
            {created.email} · {created.password}
          </p>
          <p className="mt-1 text-xs">
            The password is stored as a hash and cannot be shown again.{' '}
            <button type="button" className="underline" onClick={() => setCreated(null)}>
              Dismiss
            </button>
          </p>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Add a customer</CardTitle>
          <CardDescription>
            Creates the account and its sign-in together. Hand the password over yourself. A
            customer without a login cannot use anything you then grant them.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="c-name">Name</Label>
            <Input id="c-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-company">Company (optional)</Label>
            <Input id="c-company" value={company} onChange={(e) => setCompany(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-email">Email</Label>
            <Input id="c-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-password">Password</Label>
            <Input
              id="c-password"
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-describedby="c-password-hint"
            />
            <p id="c-password-hint" className="text-xs text-muted-foreground">
              At least 12 characters.
            </p>
          </div>
          <div className="sm:col-span-2 flex items-center gap-3">
            <Button onClick={() => create.mutate()} disabled={!canCreate}>
              {create.isPending ? <Loader2 className="animate-spin" /> : <UserPlus />} Create customer
            </Button>
            {password !== '' && password.length < 12 && (
              <span className="text-xs text-warning">Passwords must be at least 12 characters.</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pinned for the same reason as the subscriptions screen: it governs buttons far below it,
          and a disabled control with its explanation scrolled out of view reads as broken. */}
      <div className="sticky top-0 z-10 -mx-1 rounded-lg border border-border bg-background/95 px-4 py-3 backdrop-blur">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Label htmlFor="customer-reason" className="shrink-0">
            Reason
          </Label>
          <Input
            id="customer-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            className={`min-w-56 flex-1 ${reasonGiven ? '' : 'border-warning/60'}`}
          />
        </div>
        <p className={`mt-2 text-xs ${reasonGiven ? 'text-muted-foreground' : 'text-warning'}`}>
          {reasonGiven
            ? 'Recorded against whichever account you act on next. It stays here between actions.'
            : 'Required. Suspending and archiving are disabled until you enter one.'}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((filter) => (
            <Button
              key={filter.value}
              variant={status === filter.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatus(filter.value)}
            >
              {filter.label}
            </Button>
          ))}
        </div>

        <Select
          value={planId}
          onChange={(e) => setPlanId(e.target.value)}
          aria-label="Filter by plan"
          className="w-48"
        >
          <option value="">Any plan</option>
          {plans.map((plan) => (
            <option key={plan.id} value={plan.id}>
              {plan.name}
            </option>
          ))}
        </Select>

        <Select
          value={interval}
          onChange={(e) => setInterval(e.target.value as BillingInterval | '')}
          aria-label="Filter by term"
          className="w-36"
        >
          {INTERVAL_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </Select>

        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-56"
          aria-label="Search customers by name, company or ID"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {customers.isSuccess
              ? `${customers.data.data.length} customer${customers.data.data.length === 1 ? '' : 's'}`
              : 'Customers'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {customers.isPending && (
            <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading…
            </p>
          )}

          {customers.isSuccess && customers.data.data.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No customers match these filters.
            </p>
          )}

          {(customers.data?.data ?? []).map((customer) => (
            <CustomerRow
              key={customer.id}
              customer={customer}
              busy={busy}
              reasonGiven={reasonGiven}
              onAction={(fn) => act.mutate(fn)}
              onImpersonate={() => impersonate.mutate({ customerId: customer.id, why: reason })}
              reason={reason}
            />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function CustomerRow({
  customer,
  busy,
  reason,
  reasonGiven,
  onAction,
  onImpersonate,
}: {
  customer: Customer;
  busy: boolean;
  reason: string;
  reasonGiven: boolean;
  onAction: (fn: () => Promise<Customer>) => void;
  onImpersonate: () => void;
}) {
  const api = adminCustomersApi;
  const id = customer.id;
  const registered = new Date(customer.registered_at);

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/20 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium">
            {customer.name}
            {customer.company !== '' && (
              <span className="text-muted-foreground"> · {customer.company}</span>
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            {customer.subscription
              ? `${customer.subscription.plan_name} · ${customer.subscription.billing_interval_label}`
              : 'No subscription'}
            {' · signed up '}
            {registered.toLocaleDateString()} at{' '}
            {registered.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
          <p className="font-mono text-[11px] text-muted-foreground">{customer.id}</p>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant={tone[customer.status]}>{customer.status_label}</Badge>
          {customer.subscription && (
            <Badge variant={customer.subscription.grants_access ? 'success' : 'muted'}>
              {customer.subscription.status_label}
            </Badge>
          )}
        </div>
      </div>

      {customer.suspension_reason !== '' && customer.status !== 'active' && (
        <p className="text-xs text-muted-foreground">
          {customer.status_label}: {customer.suspension_reason}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {!reasonGiven && customer.status === 'active' && (
          <p className="w-full text-xs text-warning">
            Enter a reason above to enable these actions.
          </p>
        )}

        {customer.status === 'active' ? (
          <>
            {/* Read-only, short-lived, and recorded against the reason above. Offered only for
                an active account: a suspended customer's users cannot sign in, so there is no
                session to borrow. */}
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !reasonGiven}
              onClick={onImpersonate}
            >
              <Eye /> View as
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !reasonGiven}
              onClick={() => onAction(() => api.suspend(id, reason))}
            >
              <Ban /> Suspend
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-danger"
              disabled={busy || !reasonGiven}
              onClick={() => onAction(() => api.archive(id, reason))}
            >
              <Archive /> Archive
            </Button>
          </>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => onAction(() => api.restore(id))}
          >
            <Play /> Restore
          </Button>
        )}
      </div>
    </div>
  );
}
